<script setup lang="ts">
import {onBeforeUnmount, ref, watch} from "vue";
import {EyeOff, Minus, Pencil, Plus, Trash2} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Switch} from "@/core/components/ui/switch";
import CatalogIcon from "@/core/components/icon/CatalogIcon.vue";
import {AppointmentType} from "@/core/interfaces/AppointmentType";
import {
    DEFAULT_APPOINTMENT_COLOR,
    DURATION_STEP,
    MAX_DURATION,
    MIN_DURATION
} from "@/agenda/constants";

const DEBOUNCE_MS = 500;

const props = defineProps<{
    type: AppointmentType;
    disabled?: boolean;
}>();

const emits = defineEmits<{
    (e: 'update-duration', duration: number): void;
    (e: 'toggle-bookable', isBookableOnline: boolean): void;
    (e: 'edit'): void;
    (e: 'delete'): void;
}>();

/**
 * Locally displayed duration, so the stepper reacts instantly while the
 * PATCH request is debounced.
 */
const duration = ref(props.type.duration);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Value the user is waiting to see saved, held until the PATCH is emitted.
 * Reading the ref at fire time instead would lose the change: any other
 * mutation's cache write replaces the row object in between, the watch below
 * resyncs the ref to the server value, and the emit is then skipped as a
 * no-op.
 */
let pendingDuration: number | null = null;

/**
 * Resync on any refresh of the row object (cache write or refetch), so a
 * failed PATCH resets the optimistic value even when the numeric duration
 * did not change server-side. A change still queued wins: it is the user's
 * latest intent, and it has not been sent yet.
 */
watch(() => props.type, (value) => {
    if (pendingDuration !== null) {
        return;
    }

    duration.value = value.duration;
});

/**
 * Step the duration and emit the change once the user stops clicking.
 */
const stepDuration = (step: number) => {
    duration.value = Math.min(MAX_DURATION, Math.max(MIN_DURATION, duration.value + step));
    pendingDuration = duration.value;

    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => {
        debounceTimer = null;

        const target = pendingDuration;

        pendingDuration = null;

        if (target !== null && target !== props.type.duration) {
            emits('update-duration', target);
        }
    }, DEBOUNCE_MS);
};

onBeforeUnmount(() => {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }
});
</script>

<template>
  <div class="flex items-center gap-4 px-4 py-3">
    <span
      class="size-3 shrink-0 rounded-full"
      :style="{backgroundColor: type.color ?? DEFAULT_APPOINTMENT_COLOR}"
    />
    <span class="flex size-[22px] shrink-0 text-gray-dark">
      <CatalogIcon :name="type.icon" class="size-full" />
    </span>
    <p class="flex-1 truncate font-medium">
      {{ type.name }}
    </p>
    <label class="flex items-center gap-2 text-sm text-gray-500">
      Réservable en ligne
      <Switch
        :model-value="type.is_bookable_online"
        :disabled="disabled"
        @update:model-value="emits('toggle-bookable', $event)"
      />
    </label>
    <div class="flex items-center gap-1">
      <Button
        variant="outline"
        size="icon"
        class="size-7 rounded-full"
        :disabled="disabled || duration <= MIN_DURATION"
        aria-label="Réduire la durée"
        @click="stepDuration(-DURATION_STEP)"
      >
        <Minus class="size-4" />
      </Button>
      <span class="w-16 text-center text-sm tabular-nums">{{ duration }} min</span>
      <Button
        variant="outline"
        size="icon"
        class="size-7 rounded-full"
        :disabled="disabled || duration >= MAX_DURATION"
        aria-label="Augmenter la durée"
        @click="stepDuration(DURATION_STEP)"
      >
        <Plus class="size-4" />
      </Button>
    </div>
    <div class="flex items-center">
      <Button
        variant="ghost"
        size="icon"
        :disabled="disabled"
        aria-label="Modifier le type de rendez-vous"
        @click="emits('edit')"
      >
        <Pencil class="size-4" />
      </Button>
      <Button
        v-if="type.is_global"
        variant="ghost"
        size="icon"
        class="text-gray-500 hover:text-gray-700"
        :disabled="disabled"
        aria-label="Retirer de mes types"
        @click="emits('delete')"
      >
        <EyeOff class="size-4" />
      </Button>
      <Button
        v-else
        variant="ghost"
        size="icon"
        class="text-red-500 hover:text-red-600"
        :disabled="disabled"
        aria-label="Supprimer le type de rendez-vous"
        @click="emits('delete')"
      >
        <Trash2 class="size-4" />
      </Button>
    </div>
  </div>
</template>
